/**
 * <wifi-hero-stack> — 3D stacked rotation of hero product cards.
 * Direct port of the visual reference's rotation algorithm: 3 slots
 * (top / front / bottom). Every tick: top -> front -> bottom -> top.
 * order[0] = card currently at "top", order[1] = "front", order[2] = "bottom".
 *
 * Designed for exactly 3 cards (matching the 3 hero image settings), with
 * graceful static fallback for 1-2 images. Pauses on hover/focus/tab-hidden.
 * Respects prefers-reduced-motion by skipping rotation entirely and showing
 * only the front card.
 */
const POSITIONS = ['top', 'front', 'bottom'];

class WifiHeroStack extends HTMLElement {
  connectedCallback() {
    this.cards = Array.from(this.querySelectorAll('[data-hero-stack-card]'));
    if (!this.cards.length) return;

    if (this.cards.length === 1) {
      this.cards[0].setAttribute('data-pos', 'front');
      return;
    }

    if (this.cards.length === 2) {
      this.cards[0].setAttribute('data-pos', 'front');
      this.cards[1].setAttribute('data-pos', 'top');
      return;
    }

    // Image 1 starts front-and-center; the algorithm below is the reference's own.
    this.order = [1, 0, 2];
    this.interval = Number(this.getAttribute('interval')) || 2600;
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.render();

    if (this.reduced) return;

    this.addEventListener('mouseenter', () => this.pause());
    this.addEventListener('mouseleave', () => this.play());
    this.addEventListener('focusin', () => this.pause());
    this.addEventListener('focusout', () => this.play());

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.pause();
      else this.play();
    });

    this.play();
  }

  render() {
    this.cards.forEach((card) => card.removeAttribute('data-pos'));
    this.order.forEach((cardIndex, positionIndex) => {
      this.cards[cardIndex].setAttribute('data-pos', POSITIONS[positionIndex]);
    });
  }

  play() {
    if (this.timer) return;
    this.timer = setInterval(() => this.rotate(), this.interval);
  }

  pause() {
    clearInterval(this.timer);
    this.timer = null;
  }

  rotate() {
    this.order = [this.order[2], this.order[0], this.order[1]];
    this.render();
  }

  disconnectedCallback() {
    this.pause();
  }
}

customElements.define('wifi-hero-stack', WifiHeroStack);
